import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, CreditCard, Landmark, Upload, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { getPaymentOptions, getPaymentStatus, postPaymentProof, startPayment, type PayMethod, type PayStarted } from '@/api/applicant';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner, QueryState } from '@/components/kit/ui';
import { errMsg, fmtDateTime } from '@/components/kit/data';
import { naira } from '@/lib/format';
import { NeedApplication, appKeys, newKey, useApplication } from '../shared';
import { Actions, Body, Hero, Panel, Primary, Rows, Screen, Secondary } from '../ui';
import { isNativeApp, openExternal } from '@/lib/native';

const ICON = { paystack: CreditCard, school_wallet: Landmark, manual: Upload } as const;

function CopyText({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="inline-flex items-center gap-1 text-brand-600 text-xs font-semibold" onClick={() => { navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); }}>
      <Copy size={12} /> {done ? 'Copied' : 'Copy'}
    </button>
  );
}

function Account({ acct, narration }: { acct: NonNullable<PayMethod['account']>; narration?: string }) {
  return (
    <dl className="rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-200 text-sm">
      {[['Bank', acct.bank_name], ['Account number', acct.account_number], ['Account name', acct.account_name], ...(narration ? [['Narration / description', narration]] : [])].map(([k, v]) => (
        <div key={k as string} className="flex items-center justify-between gap-3 px-4 py-2.5">
          <dt className="text-slate-500">{k}</dt>
          <dd className="font-semibold text-slate-900 text-right flex items-center gap-2">{v ?? '—'}{v && (k === 'Account number' || k === 'Narration / description') && <CopyText value={String(v)} />}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PaymentPage() {
  const qc = useQueryClient();
  const app = useApplication();
  const a = app.data?.application ?? null;
  const optsQ = useQuery({ queryKey: ['applicant-pay-options'], queryFn: getPaymentOptions, enabled: !!a && a.status !== 'draft', refetchInterval: (q) => (q.state.data?.pending?.review === 'pending' ? 20000 : false) });
  const opts = optsQ.data;
  const [chosen, setChosen] = useState<PayMethod['key'] | null>(null);
  const [started, setStarted] = useState<PayStarted | null>(null);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const refresh = () => { for (const k of [appKeys.app, appKeys.status, ['applicant-pay-options']]) qc.invalidateQueries({ queryKey: k as any }); };

  // Back from Paystack: /applicant/payment?reference=APS... (Paystack also adds trxref)
  const returnRef = new URLSearchParams(window.location.search).get('reference') ?? new URLSearchParams(window.location.search).get('trxref');
  const verifyQ = useQuery({
    queryKey: ['applicant-pay-verify', returnRef], queryFn: () => getPaymentStatus(returnRef!), enabled: !!returnRef,
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 4000 : false),
  });
  useEffect(() => { if (verifyQ.data?.status === 'successful') { refresh(); window.history.replaceState(null, '', '/applicant/payment'); } }, [verifyQ.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Waiting on a transfer to the school account: poll the payment.
  const pollQ = useQuery({
    queryKey: ['applicant-pay-poll', started?.payment.id], queryFn: () => getPaymentStatus(started!.payment.id),
    enabled: started?.method === 'school_wallet', refetchInterval: (q) => (q.state.data?.status === 'successful' ? false : 8000),
  });
  useEffect(() => { if (pollQ.data?.status === 'successful') { setStarted(null); refresh(); setMsg({ tone: 'success', text: 'Payment received. Thank you!' }); } }, [pollQ.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useMutation({
    mutationFn: (m: PayMethod['key']) => startPayment(m, newKey()),
    onSuccess: (r) => {
      if (r.method === 'paystack' && r.authorization_url) {
        if (!isNativeApp) { window.location.href = r.authorization_url; return; }
        // in the app Paystack opens in the in-app browser; when it is closed, check the payment
        void openExternal(r.authorization_url, () => {
          void getPaymentStatus(r.payment.id).then((p) => {
            refresh();
            setMsg(p?.status === 'successful' ? { tone: 'success', text: 'Payment received. Thank you!' } : { tone: 'info', text: 'If you completed the payment it will show here in a moment.' });
          }).catch(() => refresh());
        });
        return;
      }
      setStarted(r); setMsg(null); refresh();
    },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  const [ref, setRef] = useState('');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const proof = useMutation({
    mutationFn: () => postPaymentProof({ transaction_reference: ref.trim(), amount: amount ? Number(amount) : undefined, paid_on: paidOn || undefined, file: file! }),
    onSuccess: (r) => { setMsg({ tone: r.status === 'approved' ? 'success' : 'info', text: r.message }); setStarted(null); refresh(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  const pending = opts?.pending;
  const inReview = pending?.review === 'pending';
  const rejected = pending?.review === 'rejected';
  const manualAcct = started?.method === 'manual' ? started.pay_to : opts?.methods.find((m) => m.key === 'manual')?.account;
  const showProofForm = (started?.method === 'manual') || (pending?.gateway === 'manual' && !inReview);

  return (
    <Screen>
      <Hero title={opts?.paid || a?.fee.paid ? 'Payment received' : 'Pay application fee'} sub={opts?.paid || a?.fee.paid ? 'Application fee' : opts ? naira(opts.amount) : undefined} />
      <Body>
      {msg && <Banner tone={msg.tone === 'info' ? 'info' : msg.tone}>{msg.text}</Banner>}
      {returnRef && (
        verifyQ.data?.status === 'successful' ? <Banner tone="success">Payment received ({naira(verifyQ.data.payment?.total_charged ?? verifyQ.data.amount)}). Your receipt reference is {verifyQ.data.reference}. <Link to="/applicant/exam" className="font-semibold underline">Go to entrance exam</Link></Banner>
          : verifyQ.isError ? <Banner>{errMsg(verifyQ.error)}</Banner>
          : <Banner tone="info">Confirming your payment with Paystack…</Banner>
      )}
      <QueryState q={app}>
        {!a ? <NeedApplication /> : a.status === 'draft' ? <NeedApplication>Submit your application form first, then pay the fee here.</NeedApplication> : (
          <QueryState q={optsQ}>
            {opts && (opts.paid || a.fee.paid ? (
              <>
                <div className="text-center pt-2"><div className="w-20 h-20 rounded-full bg-green-100 grid place-items-center mx-auto"><CheckCircle2 className="w-10 h-10 text-[#1B5E20]" /></div>
                  <p className="text-3xl font-extrabold text-[#0A2540] mt-3">{opts.amount > 0 ? `${naira(opts.amount)} paid` : 'No fee needed'}</p></div>
                <Panel><Rows rows={[
                  ['Tracking code', a.reference],
                  ...(verifyQ.data?.reference ? [['Receipt', verifyQ.data.reference] as [string, string]] : []),
                  ['Next step', a.status === 'exam_scheduled' ? 'Take entrance exam' : 'Entrance exam booking'],
                ]} /></Panel>
                <p className="text-center text-sm text-slate-500">Save your tracking code. You need it to check your application.</p>
                <Actions><Primary to="/applicant/overview">Track my application</Primary><Secondary to="/applicant/exam">Entrance exam</Secondary></Actions>
              </>
            ) : (
              <>
                <Card>
                  <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Application fee</span><span className="text-2xl font-extrabold">{naira(opts.amount)}</span></div>
                  <p className="text-xs text-slate-500 mt-1">Reference {a.reference}</p>
                </Card>

                {inReview && (
                  <Card>
                    <div className="flex items-start gap-3"><Clock className="w-6 h-6 text-amber-500 shrink-0" />
                      <div className="text-sm"><p className="font-semibold">Receipt received. The school is checking it.</p>
                        <p className="text-slate-600">Transaction ref {pending?.proof?.transaction_reference} · sent {fmtDateTime(pending?.proof?.submitted_at)}. You will be notified once it is confirmed.</p></div></div>
                  </Card>
                )}
                {rejected && <Banner><AlertTriangle className="w-4 h-4 inline mr-1" />The school could not confirm your last receipt: {pending?.rejection_reason}. Please upload a clearer receipt or the correct reference.</Banner>}

                {!inReview && !started && (
                  <Card title="Choose how to pay">
                    {opts.methods.length === 0 ? <p className="text-sm text-slate-600">{opts.office_payment} Online payment is not available for this school yet.</p> : (
                      <div className="space-y-2">
                        {opts.methods.map((m) => {
                          const Icon = ICON[m.key];
                          return (
                            <button key={m.key} type="button" onClick={() => setChosen(m.key)}
                              className={`w-full text-left flex items-start gap-3 border rounded-xl px-4 py-3 ${chosen === m.key ? 'border-green-600 bg-green-50 ring-2 ring-green-600/20' : 'border-slate-200 hover:bg-slate-50'}`}>
                              <Icon className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
                              <span className="flex-1 min-w-0"><span className="block font-semibold text-slate-900">{m.label}</span><span className="block text-xs text-slate-500">{m.hint}</span></span>
                              <span className="text-right shrink-0"><span className="block font-bold">{naira(m.total)}</span>{m.charge > 0 && <span className="block text-[11px] text-slate-500">incl. {naira(m.charge)} charge</span>}</span>
                            </button>
                          );
                        })}
                        <p className="text-xs text-slate-500 pt-1">{opts.office_payment}</p>
                        <div className="pt-2">
                          <Button className="w-full sm:w-auto" disabled={!chosen || start.isPending} onClick={() => chosen && start.mutate(chosen)}>
                            {start.isPending ? 'Please wait…' : chosen === 'paystack' ? `Pay ${naira(opts.methods.find((m) => m.key === 'paystack')?.total ?? opts.amount)}` : 'Continue'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {started?.method === 'school_wallet' && started.pay_to && (
                  <Card title="Transfer to the school account">
                    <p className="text-sm text-slate-600 mb-3">{started.instructions}</p>
                    <Account acct={started.pay_to} narration={started.narration} />
                    <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Waiting for your transfer… this page updates by itself once it arrives.</p>
                    <button className="text-xs font-semibold text-slate-600 underline mt-2" onClick={() => setStarted(null)}>Choose another way</button>
                  </Card>
                )}

                {showProofForm && manualAcct && (
                  <Card title="Transfer, then upload your receipt">
                    <p className="text-sm text-slate-600 mb-3">{started?.instructions ?? `Transfer ${naira(opts.amount)} to this account, then send us the receipt.`}</p>
                    <Account acct={manualAcct} narration={started?.narration ?? pending?.reference} />
                    <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); proof.mutate(); }}>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <label className="block sm:col-span-1"><span className="block text-xs font-semibold text-slate-600 mb-1">Transaction reference *</span>
                          <input value={ref} onChange={(e) => setRef(e.target.value)} required minLength={6} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" placeholder="From your bank receipt" /></label>
                        <label className="block"><span className="block text-xs font-semibold text-slate-600 mb-1">Amount paid</span>
                          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min={1} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" placeholder={String(opts.amount)} /></label>
                        <label className="block"><span className="block text-xs font-semibold text-slate-600 mb-1">Date paid</span>
                          <input value={paidOn} onChange={(e) => setPaidOn(e.target.value)} type="date" max={new Date().toISOString().slice(0, 10)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" /></label>
                      </div>
                      <label className="block"><span className="block text-xs font-semibold text-slate-600 mb-1">Receipt (photo or PDF, max 5 MB) *</span>
                        <input type="file" required accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-slate-100 file:font-semibold" /></label>
                      <div className="flex gap-2 flex-wrap">
                        <Button type="submit" disabled={proof.isPending || !file || ref.trim().length < 6}>{proof.isPending ? 'Sending…' : 'Send receipt'}</Button>
                        {started && <Button type="button" variant="ghost" onClick={() => setStarted(null)}>Choose another way</Button>}
                      </div>
                    </form>
                  </Card>
                )}
              </>
            ))}
          </QueryState>
        )}
      </QueryState>
      </Body>
    </Screen>
  );
}
